import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Trash2, Edit2, X, AlertTriangle } from 'lucide-react';
import { LoadingSpinner, MultiSelect } from '../../../../common';
import { AdminPageHeader, AdminCard, AdminEmptyState } from '../../../ui';
import { useAuth } from '../../../../../contexts/AuthContext';
import * as knowledgeService from '../../../../../services/knowledgeService';
import * as workspaceService from '../../../../../services/workspaceService';
import type {
  KnowledgeBaseDetailType,
  DocumentContent,
  DocumentMetadata,
} from '../../../../../types/knowledgeBase';
import type { WorkspaceGroup } from '../../../../../types/workspace';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${size} ${sizes[i]}`;
};

const formatTokenCount = (count: number): string => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K tokens`;
  }
  return `${count} tokens`;
};

const getFileTypeLabel = (type: string): string => {
  const typeMap: { [key: string]: string } = {
    txt: 'Text',
    pdf: 'PDF',
    doc: 'Word',
    docx: 'Word',
    py: 'Python',
    md: 'Markdown',
    json: 'JSON',
    yaml: 'YAML',
    yml: 'YAML',
    xml: 'XML',
    csv: 'CSV',
  };
  return typeMap[type.toLowerCase()] || type.toUpperCase();
};

export const KnowledgeBaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [database, setDatabase] = useState<KnowledgeBaseDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);
  const [showDeleteKbModal, setShowDeleteKbModal] = useState(false);
  const [isDeletingKb, setIsDeletingKb] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentContent | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editDbData, setEditDbData] = useState({
    name: '',
    description: '',
    visibility_level: 'workspace' as 'workspace' | 'group' | 'global',
    created_by_group_id: null as number | null,
    workspace_ids: [] as number[],
    group_ids: [] as number[]
  });
  const [groups, setGroups] = useState<(WorkspaceGroup & { workspace_name?: string })[]>([]);
  const [workspaces, setWorkspaces] = useState<{id: number; name: string}[]>([]);

  useEffect(() => {
    if (id) {
      fetchDatabaseDetails(parseInt(id));
    }
  }, [id]);

  // Fetch groups from ALL selected workspaces when editDbData.workspace_ids changes
  useEffect(() => {
    const fetchGroupsFromAllWorkspaces = async () => {
      const selectedWorkspaceIds = editDbData.workspace_ids;

      if (!selectedWorkspaceIds || selectedWorkspaceIds.length === 0) {
        setGroups([]);
        return;
      }

      try {
        // Fetch groups from all selected workspaces
        const groupsPromises = selectedWorkspaceIds.map(async (wsId) => {
          const response = await workspaceService.getWorkspaceGroups(wsId);
          if (response.success && response.data) {
            const workspace = workspaces.find(w => w.id === wsId);
            // Add workspace_name to each group for display
            return response.data.map(group => ({
              ...group,
              workspace_name: workspace?.name || `Workspace ${wsId}`
            }));
          }
          return [];
        });

        const allGroupsArrays = await Promise.all(groupsPromises);
        const allGroups = allGroupsArrays.flat();
        setGroups(allGroups);
      } catch (err) {
        console.error('Failed to fetch groups from workspaces:', err);
        setGroups([]);
      }
    };

    fetchGroupsFromAllWorkspaces();
  }, [editDbData.workspace_ids, workspaces]);

  // Fetch workspaces on component mount (not when modal opens)
  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const response = await workspaceService.getUserWorkspaces();
        if (response.success && response.data) {
          setWorkspaces(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch workspaces:', err);
      }
    };

    fetchWorkspaces();
  }, []); // Empty dependency array - runs only once on mount

  useEffect(() => {
    if (error && database) {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
    }
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, [error, database]);

  const fetchDatabaseDetails = async (dbId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await knowledgeService.getKnowledgeBase(dbId);
      if (response.success) {
        setDatabase(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar detalhes da base de conhecimento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilesUpload = async (files: File[] | FileList) => {
    if (!id) return;
    const list = Array.from(files);
    if (list.length === 0) return;

    try {
      setIsUploading(true);
      setUploadingCount(list.length);
      setError(null);

      const results = await Promise.allSettled(
        list.map((file) => knowledgeService.uploadDocument(parseInt(id), file))
      );

      const failed = results
        .map((r, i) => ({ r, f: list[i] }))
        .filter(({ r }) => r.status === 'rejected');

      if (failed.length > 0) {
        const names = failed.map(({ f }) => f.name).join(', ');
        setError(`Falha no upload: ${names}`);
      }

      const successful = results.filter((r) => r.status === 'fulfilled');
      if (successful.length > 0) {
        await fetchDatabaseDetails(parseInt(id));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao fazer upload dos documentos');
    } finally {
      setIsUploading(false);
      setUploadingCount(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!id) return;
    try {
      setIsDeletingDoc(true);
      setError(null);
      await knowledgeService.deleteDocument(parseInt(id), documentId);
      const response = await knowledgeService.getKnowledgeBase(parseInt(id));
      if (response.success) {
        setDatabase(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao excluir documento');
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const handleDeleteKnowledgeBase = async () => {
    if (!id) return;
    try {
      setIsDeletingKb(true);
      setError(null);
      await knowledgeService.deleteKnowledgeBase(parseInt(id));
      navigate('/admin/databases');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao excluir base de conhecimento');
      setIsDeletingKb(false);
      setShowDeleteKbModal(false);
    }
  };

  const handleDocumentClick = async (documentId: number) => {
    try {
      setIsLoadingDocument(true);
      setError(null);
      const response = await knowledgeService.getDocument(parseInt(id!), documentId);
      if (response.success) {
        setSelectedDocument(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar conteúdo do documento');
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!database) return;
    setEditDbData({
      name: database.name,
      description: database.description || '',
      visibility_level: (database.visibility_level || 'workspace') as 'workspace' | 'group' | 'global',
      created_by_group_id: database.created_by_group_id || null,
      workspace_ids: database.visible_workspace_ids || [],
      group_ids: database.visible_group_ids || []
    });
    setShowEditModal(true);
  };

  const handleEditDatabase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!database) return;
    setIsEditing(true);
    try {
      const response = await knowledgeService.updateKnowledgeBase(database.id, {
        name: editDbData.name,
        description: editDbData.description,
        visibility_level: editDbData.visibility_level,
        created_by_group_id: editDbData.visibility_level === 'group' ? editDbData.created_by_group_id : null,
        workspace_ids: editDbData.visibility_level === 'workspace' ? editDbData.workspace_ids : [],
        group_ids: editDbData.visibility_level === 'group' ? editDbData.group_ids : []
      });
      if (response.success) {
        await fetchDatabaseDetails(database.id);
        setShowEditModal(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar base de conhecimento');
    } finally {
      setIsEditing(false);
    }
  };

  const inputBaseClass = `
    w-full px-4 py-3
    bg-[#1a1a1a] border border-[#333333]
    text-white placeholder-[#888888]
    rounded-lg
    focus:outline-none focus:border-[#F4CC04]
  `;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error && !database) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <AdminEmptyState
          title="Erro ao carregar"
          description={error}
          action={
            <button
              onClick={() => id && fetchDatabaseDetails(parseInt(id))}
              className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
            >
              Tentar novamente
            </button>
          }
        />
      </div>
    );
  }

  if (!database) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <AdminEmptyState
          title="Base de conhecimento não encontrada"
          description="A base de conhecimento solicitada não existe ou foi removida"
          action={
            <button
              onClick={() => navigate('/admin/databases')}
              className="flex items-center gap-2 px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para Bases
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
      {/* Header */}
      <AdminPageHeader
        title={database.name}
        subtitle={database.description || 'Sem descrição'}
        backPath="/admin/databases"
        actions={
          <button
            onClick={handleOpenEditModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Editar
          </button>
        }
      />

      {/* Error */}
      {error && database && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center gap-6 mb-6 text-sm text-[#888888]">
        <span>
          <span className="text-[#cccccc]">Armazenamento Total:</span>{' '}
          <span className="text-white font-medium">{formatFileSize(database.total_storage)}</span>
        </span>
        <span className="text-[#333333]">•</span>
        <span>
          <span className="text-[#cccccc]">Total de Tokens:</span>{' '}
          <span className="text-white font-medium">{formatTokenCount(database.total_tokens)}</span>
        </span>
      </div>

      {/* Documents Section */}
      <AdminCard title="Documentos">
        {/* Upload Zone */}
        <div
          className={`
            mb-6 p-6 border-2 border-dashed rounded-xl cursor-pointer
            transition-colors duration-200
            ${isDragging
              ? 'border-[#F4CC04] bg-[#F4CC04]/5'
              : 'border-[#333333] hover:border-[#F4CC04] bg-[#1a1a1a]'
            }
          `}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (e.dataTransfer.files && e.dataTransfer.files.length) {
              handleFilesUpload(e.dataTransfer.files);
            }
          }}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
          }}
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <Upload className={`w-8 h-8 ${isDragging ? 'text-[#F4CC04]' : 'text-[#888888]'}`} />
            <div>
              <p className="text-white text-sm font-medium">
                {isUploading
                  ? `Enviando ${uploadingCount} arquivo(s)...`
                  : 'Arraste e solte arquivos aqui, ou clique para selecionar'
                }
              </p>
              <p className="text-[#888888] text-xs mt-1">
                Suporta TXT, PDF, DOCX, PY, MD, JSON, YAML, YML, CSV, XML
              </p>
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => e.target.files && handleFilesUpload(e.target.files)}
          disabled={isUploading}
          accept=".pdf,.docx,.txt,.py,.md,.json,.yaml,.yml,.csv,.xml"
          className="hidden"
        />

        {/* Documents List */}
        {database.documents && database.documents.length > 0 ? (
          <div className="space-y-2">
            {database.documents.map((doc: DocumentMetadata) => (
              <div
                key={doc.id}
                onClick={() => handleDocumentClick(doc.id)}
                className="
                  flex items-center justify-between p-4
                  bg-[#1a1a1a] rounded-lg
                  border border-[#333333] hover:border-[#F4CC04]
                  cursor-pointer transition-colors
                "
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-[#333333] rounded-lg">
                    <FileText className="w-5 h-5 text-[#F4CC04]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">
                      {doc.original_filename}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-[#888888] mt-1 flex-wrap">
                      <span className="px-2 py-0.5 bg-[#333333] rounded text-[#cccccc]">
                        {getFileTypeLabel(doc.file_type)}
                      </span>
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <span>{formatTokenCount(doc.token_count)}</span>
                      <span>•</span>
                      <span>
                        Adicionado {new Date(doc.created_at).toLocaleDateString('pt-BR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDelete(doc.id);
                  }}
                  disabled={isDeletingDoc}
                  className="
                    flex items-center gap-1.5 px-3 py-1.5
                    text-red-400 text-xs
                    hover:bg-red-500/10 rounded-lg transition-colors
                    disabled:opacity-50
                  "
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Excluir
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-[#333333] mx-auto mb-3" />
            <p className="text-[#888888] text-sm">Nenhum documento ainda</p>
            <p className="text-[#666666] text-xs mt-1">
              Faça upload do primeiro documento para começar
            </p>
          </div>
        )}
      </AdminCard>

      {/* Danger Zone */}
      <div className="mt-6 p-6 bg-red-500/5 border border-red-500/20 rounded-xl">
        <h3 className="text-red-400 font-semibold text-sm mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Zona de Perigo
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-medium">Excluir Base de Conhecimento</p>
            <p className="text-[#888888] text-xs mt-1">
              Excluir permanentemente esta base e todos seus documentos
            </p>
          </div>
          <button
            onClick={() => setShowDeleteKbModal(true)}
            disabled={isDeletingKb}
            className="
              flex items-center gap-2 px-4 py-2
              bg-red-500 hover:bg-red-600
              text-white text-sm font-medium
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            <Trash2 className="w-4 h-4" />
            Excluir Base
          </button>
        </div>
      </div>

      {/* Delete Document Modal */}
      {pendingDelete && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setPendingDelete(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50">
            <h3 className="text-xl font-semibold text-white mb-4">Excluir Documento</h3>
            <p className="text-[#cccccc] mb-6">
              Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={isDeletingDoc}
                className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (pendingDelete) {
                    await handleDeleteDocument(pendingDelete);
                    setPendingDelete(null);
                  }
                }}
                disabled={isDeletingDoc}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isDeletingDoc ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Knowledge Base Modal */}
      {showDeleteKbModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => !isDeletingKb && setShowDeleteKbModal(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50">
            <h3 className="text-xl font-semibold text-white mb-4">Excluir Base de Conhecimento</h3>
            <p className="text-[#cccccc] mb-6">
              Tem certeza que deseja excluir esta base de conhecimento? Todos os documentos serão permanentemente excluídos. Esta ação não pode ser desfeita.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteKbModal(false)}
                disabled={isDeletingKb}
                className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteKnowledgeBase}
                disabled={isDeletingKb}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isDeletingKb ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Excluir Base
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowEditModal(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Editar Base de Conhecimento</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditDatabase} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">Nome</label>
                <input
                  type="text"
                  placeholder="Nome da base de conhecimento"
                  value={editDbData.name}
                  onChange={(e) => setEditDbData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  className={inputBaseClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">Descrição</label>
                <textarea
                  placeholder="Descrição"
                  value={editDbData.description || ''}
                  onChange={(e) => setEditDbData((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className={`${inputBaseClass} resize-none`}
                />
              </div>

              {/* Visibility Level Selector */}
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Nível de Visibilidade
                </label>
                <select
                  value={editDbData.visibility_level}
                  onChange={(e) => {
                    const newLevel = e.target.value as 'workspace' | 'group' | 'global';
                    setEditDbData((prev) => ({
                      ...prev,
                      visibility_level: newLevel,
                      created_by_group_id: newLevel !== 'group' ? null : prev.created_by_group_id,
                      workspace_ids: (newLevel === 'workspace' || newLevel === 'group') ? prev.workspace_ids : [],
                      group_ids: newLevel === 'group' ? prev.group_ids : []
                    }));
                  }}
                  className={inputBaseClass}
                >
                  <option value="workspace">Workspace (Visível em workspaces selecionados)</option>
                  <option value="group">Grupo (Compartilhamento granular por workspaces e grupos)</option>
                  {user?.isSuperAdmin && (
                    <option value="global">Global (Visível em todos os workspaces)</option>
                  )}
                </select>
                <p className="mt-1 text-xs text-[#888888]">
                  {editDbData.visibility_level === 'global' && 'Esta base de conhecimento será visível em todos os workspaces do sistema'}
                  {editDbData.visibility_level === 'workspace' && 'Selecione os workspaces onde esta base de conhecimento estará disponível para todos os membros'}
                  {editDbData.visibility_level === 'group' && 'Modo mais granular: selecione workspaces e grupos específicos que terão acesso'}
                </p>
              </div>

              {/* Workspace Multi-Selector (if visibility_level === 'workspace' OR 'group') */}
              {(editDbData.visibility_level === 'workspace' || editDbData.visibility_level === 'group') && (
                <MultiSelect
                  label="Workspaces"
                  options={workspaces}
                  selectedIds={editDbData.workspace_ids}
                  onChange={(ids) => setEditDbData((prev) => ({ ...prev, workspace_ids: ids }))}
                  placeholder="Selecione os workspaces..."
                  required
                />
              )}

              {/* Group Multi-Selector (if visibility_level === 'group') */}
              {editDbData.visibility_level === 'group' && (
                <MultiSelect
                  label="Grupos"
                  options={groups
                    .filter((g): g is WorkspaceGroup & { workspace_name?: string } => !g.deleted)
                    .map(g => ({
                      id: g.id,
                      name: g.workspace_name ? `${g.name} - ${g.workspace_name}` : g.name
                    }))
                  }
                  selectedIds={editDbData.group_ids}
                  onChange={(ids) => setEditDbData((prev) => ({ ...prev, group_ids: ids }))}
                  placeholder="Selecione os grupos..."
                  required
                />
              )}

              <button
                type="submit"
                disabled={isEditing || !editDbData.name.trim()}
                className="w-full px-4 py-3 mt-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isEditing ? (
                  <>
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : 'Salvar Alterações'}
              </button>
            </form>
          </div>
        </>
      )}

      {/* Document Content Modal */}
      {selectedDocument && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setSelectedDocument(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl max-h-[80vh] bg-[#2a2a2a] rounded-2xl flex flex-col z-50">
            <div className="flex items-center justify-between p-6 border-b border-[#333333]">
              <h3 className="text-lg font-semibold text-white truncate pr-4">
                {selectedDocument.original_filename}
              </h3>
              <button
                onClick={() => setSelectedDocument(null)}
                className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {isLoadingDocument ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="medium" />
                </div>
              ) : (
                <pre className="text-[#cccccc] text-sm whitespace-pre-wrap font-mono bg-[#1a1a1a] p-4 rounded-lg overflow-auto">
                  {selectedDocument.content_text}
                </pre>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default KnowledgeBaseDetail;
